import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import useGoogleAdsConversion from "../hooks/useGoogleAdsConversion";

export default function BoardingSuccess() {
    const [sp] = useSearchParams();
    const navigate = useNavigate();
    const sessionId = sp.get("session_id");
    const [state, setState] = useState({ status: "checking", data: null });

    // Fire the Google Ads "conversion" event ONLY after the Stripe webhook
    // confirms payment_status='paid'. Idempotent — won't double-fire on
    // React StrictMode re-renders or polling retries.
    useGoogleAdsConversion({
        event: "boarding",
        armed: state.status === "success",
        value: state.data?.amount_total ? state.data.amount_total / 100 : 250,
        currency: (state.data?.currency || "USD").toUpperCase(),
        transactionId: sessionId,
    });

    useEffect(() => {
        if (!sessionId) {
            setState({ status: "error", data: null });
            return;
        }
        let attempts = 0;
        const poll = async () => {
            attempts++;
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setState({ status: "success", data });
                    return;
                }
                if (data.status === "expired") {
                    setState({ status: "expired", data });
                    return;
                }
            } catch (e) {
                if (attempts >= 6) {
                    setState({ status: "error", data: null });
                    return;
                }
            }
            if (attempts >= 8) {
                setState({ status: "timeout", data: null });
                return;
            }
            setTimeout(poll, 2000);
        };
        poll();
    }, [sessionId]);

    return (
        <div className="min-h-screen bg-background grain-overlay flex items-center justify-center p-6">
            <div className="glass rounded-2xl p-10 max-w-lg w-full text-center">
                {state.status === "checking" && (
                    <>
                        <Loader2 className="mx-auto animate-spin text-[hsl(45_85%_68%)] mb-4" size={48} />
                        <div className="font-heading text-2xl">Confirming your Boarding Fee...</div>
                        <div className="text-sm text-muted-foreground mt-2">Stripe is cross-checking the covenant.</div>
                    </>
                )}
                {state.status === "success" && (
                    <>
                        <CheckCircle2 className="mx-auto text-[hsl(45_85%_68%)] mb-4" size={56} />
                        <div className="font-heading text-3xl gold-gradient-text">Territory Locked</div>
                        <div className="text-sm text-muted-foreground mt-2">Welcome aboard. {state.data?.state_code} · {state.data?.sector} · {state.data?.tier?.toUpperCase()}</div>
                        <div className="mt-6 space-y-2 text-left">
                            <div className="flex justify-between text-sm border-b border-[hsl(40_40%_30%/0.2)] pb-2">
                                <span className="text-muted-foreground">State</span>
                                <span className="font-mono">{state.data?.state_code}</span>
                            </div>
                            <div className="flex justify-between text-sm border-b border-[hsl(40_40%_30%/0.2)] pb-2">
                                <span className="text-muted-foreground">Sector</span>
                                <span className="font-mono capitalize">{state.data?.sector}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Subdomain</span>
                                <span className="font-mono">{state.data?.state_code?.toLowerCase()}.{state.data?.sector}.eigooinc.net</span>
                            </div>
                        </div>
                        <button onClick={() => navigate("/app/dashboard")} className="mt-8 w-full py-3 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors" data-testid="post-payment-dashboard">
                            Enter Transparency Dashboard
                        </button>
                    </>
                )}
                {(state.status === "error" || state.status === "expired" || state.status === "timeout") && (
                    <>
                        <XCircle className="mx-auto text-[hsl(350_75%_60%)] mb-4" size={56} />
                        <div className="font-heading text-2xl">Boarding incomplete</div>
                        <div className="text-sm text-muted-foreground mt-2">
                            {state.status === "expired" ? "The checkout session expired." : "We couldn't confirm your payment. Please try again."}
                        </div>
                        <button onClick={() => navigate("/app/boarding")} className="mt-6 px-6 py-2.5 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors">
                            Try Again
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
